import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";
import { waLink } from "@/lib/whatsapp";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function lastUpdated(products) {
  const dates = products
    .map((p) => p.updated_at || p.created_at)
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return null;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(dates[dates.length - 1]));
}

export default function Landing() {
  const [updated, setUpdated] = useState(null);

  useEffect(() => {
    document.title = "ZULIANY — Achat-Revente de Luxe";
    api
      .get("/products")
      .then((res) => setUpdated(lastUpdated(res.data)))
      .catch(() => {});
  }, []);

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-white text-[#0A0A0B]">
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-6 py-20 text-center">
        <motion.img
          variants={fadeUp}
          custom={0}
          initial="hidden"
          animate="show"
          src="/logo-light.png"
          alt="Zuliany"
          data-testid="home-logo"
          className="h-44 w-44 object-contain sm:h-48 sm:w-48"
        />
        <motion.p
          variants={fadeUp}
          custom={1}
          initial="hidden"
          animate="show"
          className="mt-8 font-sarabun text-lg font-light lowercase tracking-[0.3em] text-zinc-600"
        >
          zuliany.luxury
        </motion.p>
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          animate="show"
          data-testid="home-updated"
          className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400"
        >
          {updated ? `Mise à jour — ${updated}` : "Mise à jour — très bientôt"}
        </motion.p>

        <motion.p
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate="show"
          data-testid="home-tagline"
          className="mt-10 text-sm font-light leading-relaxed text-zinc-600 sm:text-base"
        >
          Bienvenue dans l'univers ZULIANY. Accédez à mon catalogue de pièces
          de luxe authentifiées, sélectionnées avec exigence — remise en main
          propre en région toulousaine, envoi soigné sous 48h.
        </motion.p>

        <div className="mt-10 w-full space-y-3">
          <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show">
            <Link
              data-testid="home-stock-button"
              to="/stock"
              className="block w-full bg-gold-gradient px-8 py-4 text-center font-sarabun text-lg font-light tracking-[0.08em] text-[#0A0A0B] transition-transform duration-300 hover:-translate-y-0.5"
            >
              Voir le stock
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} custom={5} initial="hidden" animate="show">
            <Link
              data-testid="home-echarpe-button"
              to="/echarpes"
              className="relative block w-full overflow-hidden px-8 py-4 text-center font-sarabun text-lg font-light tracking-[0.08em] text-[#0A0A0B] transition-transform duration-300 hover:-translate-y-0.5"
            >
              <img
                src="/echarpe-fond.jpg"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <span className="absolute inset-0 bg-white/25" />
              <span className="relative">Écharpe</span>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} custom={6} initial="hidden" animate="show">
            <a
              data-testid="home-contact-button"
              href={waLink("Bonjour Zuliany, je vous contacte au sujet d'une pièce.")}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gold-gradient px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-black transition-transform duration-300 hover:-translate-y-0.5"
            >
              <span className="flex items-center justify-center gap-2">
                <WhatsAppIcon className="h-4 w-4" />
                Contact me
              </span>
            </a>
          </motion.div>
        </div>
      </main>
      <footer className="pb-10 text-center">
        <Link
          data-testid="home-admin-link"
          to="/admin"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors hover:text-zinc-500"
        >
          Espace Pro
        </Link>
      </footer>
    </div>
  );
}
